/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useRef, useState } from 'react'
import { FaGlobe } from 'react-icons/fa'

import { useLanguage } from '../i18n/LanguageProvider'

// First-visit language suggestion.
//
// We LEAD with the browser's own language preference (navigator.languages — the
// most accurate signal of what a person actually reads) and use a lightweight
// geo-IP country lookup only as a SECONDARY hint, because an IP's country is a
// poor proxy for language (travelers, expats, VPNs, shared offices). This is
// also why most sites don't do pure geo-IP language switching, and why we never
// auto-switch here: the popup only *asks*, once, and remembers the answer.
//
// If the detected language is one we already offer, we offer to switch. If it's
// a language we don't support yet, we're honest about it — the game currently
// ships in English and Portuguese — and invite the visitor to help translate.

const DISMISS_KEY = 'tr-language-suggestion-dismissed'
const GEO_CACHE_KEY = 'tr-geo-country'
const CAREERS_URL = 'https://trb.tworobots.com/careers'
const GEO_URL = 'https://get.geojs.io/v1/ip/country.json'

// Country → the site language a visitor there most likely reads. Only languages
// we actually offer on the site appear here.
const COUNTRY_TO_SITE_LANG = {
  // Portuguese
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt', CV: 'pt', GW: 'pt', ST: 'pt', TL: 'pt',
  // Simplified Chinese
  CN: 'zh-CN', SG: 'zh-CN',
  // Russian
  RU: 'ru', BY: 'ru', KZ: 'ru', KG: 'ru',
  // Japanese
  JP: 'ja',
  // German
  DE: 'de', AT: 'de', CH: 'de', LI: 'de', LU: 'de',
  // Spanish
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es',
  GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es',
  CR: 'es', PA: 'es', UY: 'es', PR: 'es', GQ: 'es'
}

// Countries whose main language we DON'T offer yet → a representative BCP-47 tag
// used only to name the language in the "help us translate" message. (Languages
// we now support — zh-CN, ru, pt, es, ja — live in COUNTRY_TO_SITE_LANG above;
// Taiwan/HK/Macau stay here because they read Traditional Chinese, not zh-CN.)
const COUNTRY_TO_OTHER_TAG = {
  TW: 'zh-TW', HK: 'zh-TW', MO: 'zh-TW',
  KR: 'ko', FR: 'fr', BE: 'fr',
  UA: 'uk', IT: 'it', NL: 'nl', PL: 'pl', TR: 'tr', TH: 'th',
  VN: 'vi', ID: 'id', SA: 'ar', AE: 'ar', EG: 'ar', IN: 'hi', SE: 'sv',
  NO: 'nb', DK: 'da', FI: 'fi', GR: 'el'
}

// Localized copy for the languages we can switch to.
const SWITCH_COPY = {
  pt: {
    title: 'Ver o site em Português?',
    body: 'Parece que você prefere Português. Podemos exibir o site em Português.',
    confirm: 'Mudar para Português',
    dismiss: 'Agora não',
    note: 'Estamos traduzindo o Two Robots para mais idiomas rapidamente. Quer ajudar?',
    careers: 'Ver vagas de tradutor →'
  },
  'zh-CN': {
    title: '切换到中文？',
    body: '看起来您可能更喜欢中文。我们可以用中文显示本网站。',
    confirm: '切换到中文',
    dismiss: '暂时不用',
    note: '我们正在快速将 Two Robots 翻译成更多语言。愿意帮忙翻译吗？',
    careers: '查看翻译职位 →'
  },
  ru: {
    title: 'Показать сайт на русском?',
    body: 'Похоже, вы предпочитаете русский. Мы можем показать сайт на русском языке.',
    confirm: 'Переключить на русский',
    dismiss: 'Не сейчас',
    note: 'Мы быстро переводим Two Robots на другие языки. Хотите помочь?',
    careers: 'Вакансии переводчиков →'
  },
  es: {
    title: '¿Ver el sitio en Español?',
    body: 'Parece que prefieres Español. Podemos mostrar el sitio en Español.',
    confirm: 'Cambiar a Español',
    dismiss: 'Ahora no',
    note: 'Estamos traduciendo Two Robots a más idiomas rápidamente. ¿Quieres ayudar?',
    careers: 'Ver vacantes de traducción →'
  },
  ja: {
    title: '日本語で表示しますか？',
    body: '日本語がお好みのようです。このサイトを日本語で表示できます。',
    confirm: '日本語に切り替える',
    dismiss: '今はしない',
    note: 'Two Robots を他の言語へ急ピッチで翻訳しています。翻訳を手伝いませんか？',
    careers: '翻訳の求人を見る →'
  },
  de: {
    title: 'Seite auf Deutsch anzeigen?',
    body: 'Sie bevorzugen anscheinend Deutsch. Wir können die Seite auf Deutsch anzeigen.',
    confirm: 'Zu Deutsch wechseln',
    dismiss: 'Nicht jetzt',
    note: 'Wir übersetzen Two Robots schnell in weitere Sprachen. Möchten Sie helfen?',
    careers: 'Übersetzer-Stellen ansehen →'
  }
}

const languageNameOf = tag => {
  if (!tag || typeof Intl === 'undefined' || !Intl.DisplayNames) return null
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'language' })
    const name = dn.of(tag)
    return name && name.toLowerCase() !== tag.toLowerCase() ? name : null
  } catch (_) {
    return null
  }
}

const browserTags = () => {
  if (typeof navigator === 'undefined') return []
  return navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language].filter(Boolean)
}

// Which site language (if any) the browser's own preferences point to.
const siteLangFromBrowser = () => {
  for (const t of browserTags()) {
    const lower = (t || '').toLowerCase()
    if (lower.startsWith('pt')) return 'pt'
    if (lower.startsWith('zh')) return 'zh-CN' // we offer Simplified only
    if (lower.startsWith('ru')) return 'ru'
    if (lower.startsWith('es')) return 'es'
    if (lower.startsWith('ja')) return 'ja'
    if (lower.startsWith('de')) return 'de'
  }
  return null
}

// The browser's top preference as a human language name, when it is NOT English
// and NOT a language we already offer (used for the "help translate" message).
const otherLangNameFromBrowser = supportedBases => {
  for (const t of browserTags()) {
    const base = (t || '').toLowerCase().split('-')[0]
    if (!base || base === 'en' || supportedBases.has(base)) continue
    const name = languageNameOf(t)
    if (name) return name
  }
  return null
}

const fetchCountry = async () => {
  // Cache the country so repeat visits (common for English-in-an-English-country
  // visitors, who never see a popup) don't re-hit the geo-IP service each load.
  try {
    const cached = window.localStorage.getItem(GEO_CACHE_KEY)
    if (cached) return cached
  } catch (_) {
    /* storage blocked — fall through to the network */
  }
  if (typeof fetch === 'undefined') return null
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), 3500) : null
  try {
    const res = await fetch(GEO_URL, {
      signal: controller ? controller.signal : undefined
    })
    if (!res.ok) return null
    const data = await res.json()
    const country =
      (data && data.country && String(data.country).toUpperCase()) || null
    if (country) {
      try {
        window.localStorage.setItem(GEO_CACHE_KEY, country)
      } catch (_) {
        /* ignore */
      }
    }
    return country
  } catch (_) {
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const LanguageSuggestion = () => {
  const { language, setLanguage, languages } = useLanguage()
  const [suggestion, setSuggestion] = useState(null) // { kind:'switch', code } | { kind:'other', name }
  const [visible, setVisible] = useState(false)
  const [isNarrow, setIsNarrow] = useState(false)
  const decidedRef = useRef(false)

  // Track viewport width so the card can dodge the bottom-right playtest bubble
  // on desktop (bottom-left) and go full-width on phones.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const onResize = () => setIsNarrow(window.innerWidth <= 640)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (decidedRef.current) return undefined
    // Only suggest from the default English view, and never nag twice.
    if (language !== 'en') return undefined
    try {
      if (window.localStorage.getItem(DISMISS_KEY)) return undefined
    } catch (_) {
      /* storage blocked — proceed */
    }

    let cancelled = false
    const supportedCodes = new Set(languages.map(l => l.code))
    const supportedBases = new Set(
      languages.map(l => l.code.toLowerCase().split('-')[0])
    )

    const decide = async () => {
      let result = null

      // 1) Browser language → a site language we can switch to (best signal).
      const browserLang = siteLangFromBrowser()
      if (browserLang && browserLang !== 'en' && supportedCodes.has(browserLang)) {
        result = { kind: 'switch', code: browserLang }
      }

      // 2) Geo-IP country — only if the browser didn't already decide it.
      if (!result) {
        const country = await fetchCountry()
        const countryLang = country ? COUNTRY_TO_SITE_LANG[country] : null
        if (
          countryLang &&
          supportedCodes.has(countryLang) &&
          countryLang !== language
        ) {
          result = { kind: 'switch', code: countryLang }
        } else {
          // 3) A language we don't offer yet → invite them to help translate.
          const name =
            otherLangNameFromBrowser(supportedBases) ||
            (country && COUNTRY_TO_OTHER_TAG[country]
              ? languageNameOf(COUNTRY_TO_OTHER_TAG[country])
              : null)
          if (name) result = { kind: 'other', name }
        }
      }

      if (cancelled || !result) return
      decidedRef.current = true
      setSuggestion(result)
      window.setTimeout(() => {
        if (!cancelled) setVisible(true)
      }, 1200)
    }

    decide()
    return () => {
      cancelled = true
    }
  }, [language, languages])

  const remember = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1')
    } catch (_) {
      /* ignore */
    }
  }

  const handleSwitch = () => {
    remember()
    setVisible(false)
    if (suggestion?.code) setLanguage(suggestion.code)
  }

  const handleDismiss = () => {
    remember()
    setVisible(false)
  }

  if (!suggestion) return null

  const isSwitch = suggestion.kind === 'switch'
  const copy = isSwitch ? SWITCH_COPY[suggestion.code] : null
  const flag = isSwitch
    ? languages.find(l => l.code === suggestion.code)?.flag
    : null

  const wrapStyle = {
    position: 'fixed',
    zIndex: 9999,
    bottom: isNarrow
      ? 'calc(12px + env(safe-area-inset-bottom, 0px))'
      : '20px',
    left: isNarrow ? '12px' : '20px',
    right: isNarrow ? '12px' : 'auto',
    width: isNarrow ? 'auto' : 'min(380px, calc(100vw - 40px))',
    transform: visible ? 'translateY(0)' : 'translateY(16px)',
    opacity: visible ? 1 : 0,
    transition: 'opacity .4s cubic-bezier(.22,1,.36,1), transform .4s cubic-bezier(.22,1,.36,1)',
    pointerEvents: visible ? 'auto' : 'none'
  }

  const cardStyle = {
    position: 'relative',
    borderRadius: '16px',
    padding: '16px 16px 14px',
    background:
      'linear-gradient(150deg, rgba(17,20,26,0.96) 0%, rgba(11,13,18,0.97) 100%)',
    border: '1px solid rgba(120,214,220,0.28)',
    boxShadow: '0 16px 48px rgba(0,0,0,0.55), 0 0 22px rgba(38,220,255,0.08)',
    backdropFilter: 'blur(14px) saturate(140%)',
    WebkitBackdropFilter: 'blur(14px) saturate(140%)',
    color: '#eceae4'
  }

  const primaryBtn = {
    flex: isSwitch ? 1 : 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    height: '40px',
    padding: '0 16px',
    borderRadius: '9px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: "'industryMedium', system-ui, sans-serif",
    fontSize: '13px',
    fontWeight: 700,
    color: '#06222a',
    background: 'linear-gradient(140deg, #8fe7ec 0%, #26dcff 60%, #1aa7c4 100%)',
    textDecoration: 'none'
  }

  const dismissBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    padding: '0 14px',
    borderRadius: '9px',
    cursor: 'pointer',
    fontFamily: "'industryMedium', system-ui, sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    color: 'rgba(236,234,228,0.8)',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.16)'
  }

  return (
    <div
      className="notranslate"
      translate="no"
      role="dialog"
      aria-label="Language suggestion"
      style={wrapStyle}
    >
      <div style={cardStyle}>
        {/* close */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          style={{
            position: 'absolute',
            top: '9px',
            right: '9px',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.16)',
            color: 'rgba(255,255,255,0.75)',
            cursor: 'pointer',
            padding: 0
          }}
        >
          <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden>
            <path
              d="M1 1L9 9M9 1L1 9"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            marginBottom: '7px',
            paddingRight: '24px'
          }}
        >
          <span style={{ fontSize: '20px', lineHeight: 1 }} aria-hidden>
            {flag || <FaGlobe style={{ color: '#8fe7ec' }} />}
          </span>
          <span
            style={{
              fontFamily: "'Aldrich', system-ui, sans-serif",
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.01em'
            }}
          >
            {isSwitch ? copy.title : `Coming soon in ${suggestion.name}`}
          </span>
        </div>

        {/* body */}
        <p
          style={{
            margin: '0 0 12px',
            fontFamily: "'industryMedium', system-ui, sans-serif",
            fontSize: '12.5px',
            lineHeight: 1.45,
            color: 'rgba(224,228,230,0.82)'
          }}
        >
          {isSwitch
            ? copy.body
            : `Two Robots isn't available in ${suggestion.name} yet, but we're rapidly adding new languages.`}
        </p>

        {/* actions */}
        {isSwitch ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleSwitch} style={primaryBtn}>
              {copy.confirm}
            </button>
            <button type="button" onClick={handleDismiss} style={dismissBtn}>
              {copy.dismiss}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <a
              href={CAREERS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDismiss}
              style={primaryBtn}
            >
              Help us translate →
            </a>
            <button type="button" onClick={handleDismiss} style={dismissBtn}>
              Dismiss
            </button>
          </div>
        )}

        {/* translator note — shown on the switch card too, since the game itself
            currently ships only in English and Portuguese */}
        {isSwitch && (
          <div
            style={{
              marginTop: '11px',
              paddingTop: '10px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              fontFamily: "'industryMedium', system-ui, sans-serif",
              fontSize: '11px',
              lineHeight: 1.45,
              color: 'rgba(224,228,230,0.62)'
            }}
          >
            {copy.note}{' '}
            <a
              href={CAREERS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDismiss}
              style={{ color: '#8fe7ec', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {copy.careers}
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

export default LanguageSuggestion
